import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Outlet, useNavigate } from 'react-router-dom';
import { SideNavBar } from '@/versions/v3/features/home/components/NavigationBar';
import useAuth from '@/hooks/useAuth';
import useXero from '@/hooks/useXero';
import LoadingScreen from '@/pages/common/LoadingScreen';
import XeroModal from '@/features/home/components/XeroModal';
import XeroTenantModal from '@/features/home/components/XeroTenantModal';
import { routes } from '@/utils/routes';
import { useWallet } from '@/features/home/hooks/useWallet';
import useHomeContext from '@/hooks/useHomeContext';
import { getTeamMembers } from '@/services/business';
import { TEAM_MEMBERS_QUERY_KEY } from '@/features/teamMembers/hooks/useTeamMembers';
import { getGeneralLedgerCodes } from '@/services/generalLedgerCodes';
import { useXeroConnection } from '@/features/home/hooks/useXeroConnection';
import ExportToast from '@/features/home/components/ExportToast';
import NotificationToasts from '@/features/home/components/NotificationToasts';
import IdleTimeoutModal from '@/features/home/components/IdleTimeoutModal';
import { useUserActivity } from '@/features/home';
import { usePasskeySetupPrompt } from '@/features/passkeySettings/hooks/usePasskeySetupPrompt';
import PasskeySetupPromptModal from '@/features/passkeySettings/components/PasskeySetupPromptModal';

const LAYOUT = 'flex h-screen bg-white';
const DIVIDER = 'w-px bg-emerge-navy-4';
const MAIN = 'flex-1 flex flex-col overflow-hidden';
const CONTENT = 'flex-1 overflow-auto';

const Home = () => {
  const { customer, accountId, business } = useAuth()!;
  const { showIdleModal, stayActive, getRemainingTime } = useUserActivity();
  const xeroContext = useXero();
  const navigate = useNavigate();
  const [xeroModalOpen, setXeroModalOpen] = useState(false);
  const { allWallets, allOptions } = useWallet();
  const {
    setTeamMembers,
    setTransactionGeneralLedgerCodes,
    setOriginalTransactionGeneralLedgerCodes,
    originalTransactionGeneralLedgerCodes,
    generalLedgerCodeSearchInput
  } = useHomeContext()!;
  const { xeroTenants, openXeroTenantsModal, setOpenXeroTenantsModal, setIsXeroConnected } =
    useXeroConnection();
  const {
    isOpen: showPasskeySetupPrompt,
    isCreating: isCreatingPasskey,
    handleNotNow: handlePasskeySetupNotNow,
    handleCreatePasskey: handlePasskeySetupCreate
  } = usePasskeySetupPrompt();

  const handleXeroConnectionStatus = (isConnected: boolean) => {
    setIsXeroConnected(isConnected);
  };

  // Shares its cache with useTeamMembers.ts (same query key) - the Team Members page fetches
  // this exact same business-scoped list itself, so without a shared key, visiting it would
  // issue a second, fully redundant request for data this layout already has in hand. Whichever
  // of the two mounts first populates the cache; the other reuses it (staleTime: Infinity, see
  // queryClient.ts), so at most one request is ever made per session.
  const teamMembersQuery = useQuery(
    [TEAM_MEMBERS_QUERY_KEY, business?.id],
    () => getTeamMembers(business!.id).then((res) => res.data),
    { enabled: Boolean(business?.id) }
  );

  useEffect(() => {
    if (!teamMembersQuery.data) return;
    setTeamMembers(
      teamMembersQuery.data.map((member) => {
        const firstName = member.preferredName ?? member.firstName;
        return {
          avatar: undefined,
          name: [firstName, member.lastName].join(' '),
          id: member.customerId
        };
      })
    );
  }, [teamMembersQuery.data]);

  useEffect(() => {
    if (!business) return;

    const fetchGeneralLedgerCodes = async () => {
      const res = await getGeneralLedgerCodes(business.id, { archived: false });
      const codes = res.data.map((code) => ({
        id: code.id,
        name: code.name,
        color: code.color,
        code: code.code,
        category: code.category
      }));
      setOriginalTransactionGeneralLedgerCodes(codes);
      setTransactionGeneralLedgerCodes(codes);
    };

    fetchGeneralLedgerCodes();
  }, [business]);

  useEffect(() => {
    if (!business) return;
    if (generalLedgerCodeSearchInput.length >= 0 && generalLedgerCodeSearchInput.length <= 50) {
      setTransactionGeneralLedgerCodes(
        originalTransactionGeneralLedgerCodes.filter(
          (code) =>
            code.code.toLowerCase().includes(generalLedgerCodeSearchInput.toLowerCase()) ||
            code.name.toLowerCase().includes(generalLedgerCodeSearchInput.toLowerCase())
        )
      );
    } else {
      setTransactionGeneralLedgerCodes(originalTransactionGeneralLedgerCodes);
    }
  }, [business, generalLedgerCodeSearchInput, originalTransactionGeneralLedgerCodes]);

  const handleOpenXero = useCallback(() => {
    if (xeroContext?.xeroTenant) {
      navigate(routes.xero);
    } else {
      setXeroModalOpen(true);
    }
  }, [xeroContext?.xeroTenant, navigate]);

  if (!customer) {
    return <LoadingScreen />;
  }

  return (
    <div className={LAYOUT}>
      <SideNavBar onOpenXeroModal={handleOpenXero} />
      <div className={DIVIDER} />
      <main className={MAIN}>
        <ExportToast />
        <NotificationToasts />
        <div className={CONTENT}>
          <Outlet context={{ accountId, allWallets, allOptions, handleXeroConnectionStatus }} />
        </div>
      </main>
      <IdleTimeoutModal
        isOpen={showIdleModal}
        onStayActive={stayActive}
        getRemainingTime={getRemainingTime}
      />
      <XeroModal isOpen={xeroModalOpen} onClose={() => setXeroModalOpen(false)} />
      <XeroTenantModal
        isOpen={openXeroTenantsModal}
        tenants={xeroTenants}
        onClose={() => setOpenXeroTenantsModal(false)}
      />
      <PasskeySetupPromptModal
        isOpen={showPasskeySetupPrompt}
        isCreating={isCreatingPasskey}
        onNotNow={handlePasskeySetupNotNow}
        onCreatePasskey={handlePasskeySetupCreate}
      />
    </div>
  );
};

export default Home;
